import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const navLinks = [
    { path: '/', label: 'Startseite', icon: 'ri-home-4-line' },
    { path: '/leistungen', label: 'Leistungen', icon: 'ri-service-line' },
    { path: '/hausmeister', label: 'Hausmeister', icon: 'ri-tools-line' },
    { path: '/schluesseldienst', label: 'Schlüsseldienst', icon: 'ri-key-line' },
    { path: '/jobs', label: 'Jobs', icon: 'ri-briefcase-line' },
    { path: '/kontakt', label: 'Kontakt', icon: 'ri-mail-line' }
  ];

  return (
    <>
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-white lg:bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center">
              <img
                src="https://static.readdy.ai/image/42a59d03aabdb3bbb4adf980fce8c22a/c496e7b7b0d35cec41af6fd81216106e.png"
                alt="IGM Facility Service Logo"
                className="h-12 w-auto object-contain"
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-8">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`text-sm font-medium transition-colors whitespace-nowrap ${
                    location.pathname === link.path
                      ? 'text-[#0D2B4E]'
                      : isScrolled
                      ? 'text-gray-700 hover:text-[#0D2B4E]'
                      : 'text-white hover:text-white/70'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </nav>

            {/* Phone CTA */}
            <a
              href="tel:+492102739807"
              className="hidden lg:flex items-center space-x-2 bg-[#0D2B4E] text-white px-6 py-3 rounded-lg hover:bg-[#0a2240] transition-colors whitespace-nowrap"
            >
              <i className="ri-phone-line text-lg"></i>
              <span className="font-semibold">02102 7398070</span>
            </a>

            {/* Mobile Menu Button - Größer für bessere Touch-Targets */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden w-12 h-12 flex items-center justify-center cursor-pointer rounded-lg hover:bg-gray-100 transition-colors"
              aria-label="Menü öffnen"
            >
              <i className={`text-2xl ${isScrolled ? 'text-gray-900' : 'text-gray-900 lg:text-white'} ${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu Drawer */}
      <div
        className={`fixed inset-0 z-40 lg:hidden transition-opacity duration-300 ${
          isMobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
      >
        <div className="absolute inset-0 bg-black/50" onClick={() => setIsMobileMenuOpen(false)}></div>
        <div
          className={`absolute top-0 right-0 bottom-0 w-80 max-w-[85vw] bg-white shadow-xl transform transition-transform duration-300 ${
            isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          <div className="flex flex-col h-full">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <Link to="/" className="flex items-center">
                <img
                  src="https://static.readdy.ai/image/42a59d03aabdb3bbb4adf980fce8c22a/c496e7b7b0d35cec41af6fd81216106e.png"
                  alt="IGM Facility Service Logo"
                  className="h-10 w-auto object-contain"
                />
              </Link>
              <button 
                onClick={() => setIsMobileMenuOpen(false)} 
                className="w-12 h-12 flex items-center justify-center cursor-pointer rounded-lg hover:bg-gray-100 transition-colors"
                aria-label="Menü schließen"
              >
                <i className="ri-close-line text-2xl text-gray-900"></i>
              </button>
            </div>

            {/* Navigation Links */}
            <nav className="flex-1 overflow-y-auto p-4">
              <div className="space-y-1">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`flex items-center gap-3 px-4 py-4 rounded-lg text-base font-medium transition-all ${
                      location.pathname === link.path
                        ? 'bg-[#0D2B4E] text-white shadow-md'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <i className={`${link.icon} text-xl`}></i>
                    <span>{link.label}</span>
                  </Link>
                ))}
              </div>
            </nav>

            {/* Phone CTA - Prominent am unteren Rand */}
            <div className="p-4 border-t border-gray-200 bg-gray-50">
              <a
                href="tel:+492102739807"
                className="flex items-center justify-center gap-3 bg-[#0D2B4E] text-white px-6 py-4 rounded-lg hover:bg-[#0a2240] transition-colors w-full whitespace-nowrap shadow-lg"
              >
                <i className="ri-phone-fill text-xl"></i>
                <span className="font-bold text-lg">02102 7398070</span>
              </a>
              <p className="text-center text-xs text-gray-500 mt-3">24/7 Notdienst verfügbar</p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}